import { normalizeFitRef, type PublicConfigSummary } from "@cfg/api-contract";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useEffect, useId, useState } from "react";
import { useSearchParams } from "react-router";
import { useApp } from "../app-context.tsx";
import { copyFirst } from "../brand.ts";
import { PageHead } from "./PageHead.tsx";
import { DefaultCatalogCard, DefaultEmptyState } from "./defaults.tsx";

/** Published configurations, searchable by fit (ref, base number or name) and filterable by design. */
export function CommunityPage() {
  const { api, brand, copy, registry } = useApp();
  const Card = brand.slots?.CatalogCard ?? DefaultCatalogCard;
  const Empty = brand.slots?.EmptyState ?? DefaultEmptyState;
  const [search, setSearch] = useSearchParams();
  const fit = search.get("fit") ?? "";
  const design = search.get("design") ?? "";
  const [text, setText] = useState(fit);
  const ids = useId();
  // Back/forward and chip clicks change the URL; keep the field in step.
  useEffect(() => setText(fit), [fit]);

  const catalog = useQuery({ queryKey: ["catalog", brand.config.id], queryFn: () => registry.catalog() });
  const fits = useQuery({ queryKey: ["fits", brand.config.id], queryFn: () => api!.listFits(), enabled: !!api });
  const feed = useInfiniteQuery({
    queryKey: ["community", brand.config.id, fit, design],
    queryFn: ({ pageParam }) => api!.listPublic({ fit: fit || undefined, design: design || undefined, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.cursor,
    enabled: !!api,
  });

  if (!api) return <Empty title={copy("community.title")} body="This deployment has no API configured." />;

  const designs = new Map((catalog.data ?? []).map((d) => [d.id, d]));
  const items: PublicConfigSummary[] = feed.data?.pages.flatMap((p) => p.items) ?? [];
  const matched = feed.data?.pages[0]?.matched ?? [];
  const searchLabel = copyFirst(brand.config, copy, ["community.searchLabel", "community.search"]);
  // The legacy key was often a noun ("Set number"); the button is always the action key.
  const searchAction = copy("community.searchAction");
  const setFilter = (next: { fit?: string; design?: string }) => {
    const p = new URLSearchParams();
    // Keep what was typed (trimmed): the API resolves refs, base numbers and names.
    const f = (next.fit ?? fit).trim().replace(/\s+/g, " ");
    const d = next.design ?? design;
    if (f) p.set("fit", f);
    if (d) p.set("design", d);
    setSearch(p);
  };
  const clearAll = () => {
    setText("");
    setSearch(new URLSearchParams());
  };
  const pickHref = design ? `/design/${encodeURIComponent(design)}` : "/";
  // Show what a search resolved to when it is not just the typed ref.
  const showMatched = !!fit && matched.length > 0 && !(matched.length === 1 && matched[0] === normalizeFitRef(fit));

  return (
    <section className="page page-community">
      <PageHead eyebrow={copy("community.eyebrow")} title={copy("community.title")} sub={copy("community.sub")}>
        <form
          className="community-filters"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            setFilter({ fit: text });
          }}
        >
          <label className="community-field community-field-search" htmlFor={`${ids}-q`}>
            <span className="community-field-label">{searchLabel}</span>
            <input
              id={`${ids}-q`}
              className="cfg-input community-search-input"
              type="search"
              enterKeyHint="search"
              placeholder={brand.config.fitTags.refPlaceholder || searchLabel}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </label>
          <label className="community-field community-field-design" htmlFor={`${ids}-d`}>
            <span className="community-field-label">{copy("community.designLabel")}</span>
            <select id={`${ids}-d`} className="cfg-input community-design-select" value={design} onChange={(e) => setFilter({ design: e.target.value })}>
              <option value="">{copy("community.allDesigns")}</option>
              {(catalog.data ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.title}
                </option>
              ))}
            </select>
          </label>
          <div className="community-filter-actions">
            <button type="submit" className="cfg-btn community-search-btn">
              {searchAction}
            </button>
            {(fit || design) && (
              <button type="button" className="cfg-btn community-clear" aria-label={copy("community.clearLabel")} onClick={clearAll}>
                <span aria-hidden="true">× </span>
                {copy("community.clear")}
              </button>
            )}
          </div>
        </form>
        {fits.data && fits.data.fits.length > 0 && (
          <div className="community-popular">
            <span className="cfg-presets-label">{copy("community.popular")}</span>
            {fits.data.fits.slice(0, 20).map((f) => (
              <button
                key={f.ref}
                type="button"
                className={`cfg-chip${f.ref === normalizeFitRef(fit) ? " is-active" : ""}`}
                title={f.name}
                aria-pressed={f.ref === normalizeFitRef(fit)}
                onClick={() => setFilter({ fit: f.ref })}
              >
                {f.ref} ({f.count})
              </button>
            ))}
          </div>
        )}
        {showMatched && (
          <p className="community-matched muted small" aria-live="polite">
            {copy("community.matched", { refs: matched.join(", ") })}
          </p>
        )}
      </PageHead>

      {feed.isLoading && <p className="muted">Loading…</p>}
      {feed.isError && <Empty title="Could not load community configurations" body={String(feed.error)} />}
      {feed.isSuccess && items.length === 0 &&
        (fit || design ? (
          <Empty
            title={fit ? copy("community.noMatch", { ref: fit }) : copy("community.noMatchDesign")}
            body={copy("community.noMatchBody")}
            action={
              <div className="community-empty-actions">
                <button type="button" className="cfg-btn community-clear-filter" onClick={clearAll}>
                  {copy("community.clearFilter")}
                </button>
                <a className="cfg-btn cfg-btn-primary community-pick" href={pickHref}>
                  {copy("community.pickDesign")}
                </a>
              </div>
            }
          />
        ) : (
          <Empty title={copy("community.empty")} />
        ))}
      {items.length > 0 && (
        <div className="catalog-grid community-grid">
          {items.map((it) => {
            const d = designs.get(it.designId);
            const designTitle = d?.title ?? it.designId;
            return (
              <Card
                key={it.id}
                entry={{
                  id: it.id,
                  version: it.version,
                  title: it.name,
                  // Joined summary for brands that only render `description`.
                  description: [
                    designTitle,
                    copy("community.by", { name: it.authorName }),
                    it.printed ? `✓ ${copy("community.printed")}` : null,
                    it.confirmCount > 0 ? copy("community.confirms", { count: it.confirmCount }) : null,
                  ]
                    .filter(Boolean)
                    .join(" · "),
                  tags: it.fits.map((f) => f.ref),
                  thumbnailUrl: d?.thumbnailUrl,
                  href: `/community/${encodeURIComponent(it.id)}`,
                  kind: "community",
                  designTitle,
                  author: it.authorName,
                  fits: it.fits.map((f) => ({ ref: f.ref, name: f.name })),
                  fitLabel: brand.config.fitTags.label,
                  printed: it.printed,
                  confirmCount: it.confirmCount,
                  updatedAt: it.updatedAt,
                }}
              />
            );
          })}
        </div>
      )}
      {feed.hasNextPage && (
        <p className="community-more">
          <button type="button" className="cfg-btn" disabled={feed.isFetchingNextPage} onClick={() => void feed.fetchNextPage()}>
            {copy("community.more")}
          </button>
        </p>
      )}
    </section>
  );
}
